import { Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  useListAccessGrantsQuery,
  useListDepartmentsQuery,
  useListOrganizationUsersQuery,
} from "../../../../autogen/bff-api";
import { Paging } from "../../../../common/Paging";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { displayPersonName } from "../../../../common/persons/displayPersonName";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { SettingsCard } from "./SettingsCard";
import { UserRow } from "./UserRow";
import { User } from "./types";

export const Users = () => {
  const authState = useLoggedInWithOrgContextState();
  const orgId = authState.selectedOrg.id;
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const confirmationModal = useConfirmationModal();

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      name: params.get("name") || "",
      skip: +(params.get("skip") || 0),
      limit: +(params.get("limit") || 10),
    };
  });

  useEffect(() => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        Object.entries(filters).forEach(([key, value]) => {
          if (value || value === 0) params.set(key, `${value}`);
          else params.delete(key);
        });
        return params;
      },
      { replace: true },
    );
  }, [filters, setSearchParams]);

  const { data, isLoading } = useListOrganizationUsersQuery({
    name: filters.name,
    limit: filters.limit,
    skip: filters.skip,
  });
  const { data: departmentData } = useListDepartmentsQuery({ orgId });
  const { data: grantData } = useListAccessGrantsQuery({ orgId });

  const hasDepartments = (departmentData?.departments.length ?? 0) > 1;
  const grantsByPerson = useMemo(() => {
    const byPerson = new Map<string, NonNullable<typeof grantData>["grants"]>();
    (grantData?.grants ?? []).forEach((grant) => {
      byPerson.set(grant.personId, [
        ...(byPerson.get(grant.personId) ?? []),
        grant,
      ]);
    });
    return byPerson;
  }, [grantData]);

  const users: User[] = useMemo(
    () =>
      data?.users?.map((e) => ({
        id: e.person.id,
        name: displayPersonName(e.person),
        email: e.person.email,
        roles: e.roles,
      })) ?? [],
    [data],
  );

  return (
    <>
      {confirmationModal.modal}
      <Flex alignItems="center">
        <InputGroup mr="5px">
          <InputLeftElement pointerEvents="none">
            <Search2Icon color="smMuted" />
          </InputLeftElement>
          <Input
            type="text"
            placeholder={`${t("Search user by name or email")}...`}
            value={filters.name}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                name: e.target.value,
                skip: 0,
              }))
            }
          />
        </InputGroup>
        <Text
          display={{ base: "none", sm: "block" }}
          fontSize="sm"
          color="smMuted"
          fontWeight="semibold"
          ml="-28"
        >
          {data?.count} {t("users")}
        </Text>
      </Flex>
      <Paging
        limit={filters.limit}
        skip={filters.skip}
        setSkip={(skip) => setFilters((prev) => ({ ...prev, skip }))}
        isLoading={isLoading}
        count={data?.count}
      />
      <SettingsCard
        title={t("Users")}
        subtitle={t("Everyone with access in this organization")}
        inset={false}
      >
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            width="full"
            minWidth="640px"
            style={{ tableLayout: "fixed" }}
          >
            <Thead>
              <Tr>
                <Th pl="5" borderBottomColor="smBorderSubtle">
                  {t("Name")}
                </Th>
                <Th borderBottomColor="smBorderSubtle">{t("Access")}</Th>
                <Th pr="5" borderBottomColor="smBorderSubtle" width="190px" />
              </Tr>
            </Thead>
            <Tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  confirmationModal={confirmationModal}
                  showDepartments={hasDepartments}
                  departmentGrants={grantsByPerson.get(u.id) ?? []}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
        {!users.length && (
          <Box px="5" py="6">
            <Text fontSize="sm" color="smMuted">
              {isLoading
                ? `${t("Loading users")}...`
                : `${t("No users have been added")}.`}
            </Text>
          </Box>
        )}
      </SettingsCard>
    </>
  );
};
